interface TelegramBackButton {
  show(): void;
  hide(): void;
  onClick(callback: () => void): void;
  offClick(callback: () => void): void;
}

interface TelegramViewportChangedEvent {
  isStateStable: boolean;
}

interface TelegramEventMap {
  themeChanged: undefined;
  viewportChanged: TelegramViewportChangedEvent;
}

type TelegramEventHandler<Event extends keyof TelegramEventMap> =
  TelegramEventMap[Event] extends undefined
    ? () => void
    : (payload: TelegramEventMap[Event]) => void;

/** Палитра клиента Telegram. Приходит не полностью: часть полей зависит от версии. */
export interface TelegramThemeParams {
  bg_color?: string;
  secondary_bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  header_bg_color?: string;
  section_bg_color?: string;
  destructive_text_color?: string;
}

interface TelegramWebApp {
  initData: string;
  /** Разобранная часть `initData`: нужен только `start_param` из ссылки. */
  initDataUnsafe?: { start_param?: string };
  version: string;
  colorScheme: 'light' | 'dark';
  themeParams: TelegramThemeParams;
  viewportHeight?: number;
  viewportStableHeight?: number;
  isExpanded?: boolean;
  ready(): void;
  expand(): void;
  close(): void;
  isVersionAtLeast?(version: string): boolean;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  disableVerticalSwipes?(): void;
  onEvent?<Event extends keyof TelegramEventMap>(
    event: Event,
    callback: TelegramEventHandler<Event>,
  ): void;
  offEvent?<Event extends keyof TelegramEventMap>(
    event: Event,
    callback: TelegramEventHandler<Event>,
  ): void;
  BackButton: TelegramBackButton;
  HapticFeedback?: {
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    impactOccurred(style: 'light' | 'medium' | 'heavy'): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function telegram(): TelegramWebApp | undefined {
  return window.Telegram?.WebApp;
}

/** `true`, когда страница открыта внутри Telegram, а не в обычном браузере. */
export function isInsideTelegram(): boolean {
  return Boolean(telegram()?.initData);
}

/**
 * SDK Telegram на странице есть, но подписанных данных он не дал. Так бывает,
 * когда Mini App открыли прямой ссылкой, а не кнопкой бота: войти нечем, и
 * человеку нужно сказать об этом словами, а не показывать поле для токена.
 */
export function hasTelegramRuntime(): boolean {
  return Boolean(telegram());
}

/**
 * Подготовка Mini App: сообщаем клиенту о готовности, разворачиваем на весь
 * экран и подстраиваемся под тему. Всё вызывается защищённо — старые версии
 * Telegram не знают части методов, а в браузере SDK нет вовсе.
 */
export function initTelegram(): void {
  const app = telegram();

  if (!app) {
    applyColorScheme(preferredBrowserScheme());
    return;
  }

  app.ready();
  app.expand();

  // Вертикальный свайп по списку не должен закрывать приложение (Bot API 7.7+).
  app.disableVerticalSwipes?.();

  syncTheme(app);
  syncViewport(app);

  app.onEvent?.('themeChanged', () => syncTheme(app));
  app.onEvent?.('viewportChanged', (event) => {
    syncViewport(app);

    if (
      shouldExpandModalViewport(
        app.isExpanded,
        event.isStateStable,
        document.documentElement.classList.contains('is-modal-open'),
      )
    ) {
      // Telegram игнорирует expand() во время самого свайпа. Вызываем его
      // после стабильного viewportChanged, уже за пределами обработчика.
      window.setTimeout(() => {
        if (
          app.isExpanded === false &&
          document.documentElement.classList.contains('is-modal-open')
        ) {
          app.expand();
        }
      }, 0);
    }
  });
}

/** Совпадает с `TELEGRAM_ARTIST_PREFIX` в `apps/api/src/artists/artists.service.ts`. */
const ARTIST_START_PREFIX = 'artist_';

/**
 * Ссылка `t.me/бот?startapp=artist_<slug>` открывает Mini App и приводит на
 * страницу художника. Так заявка уходит с подписанным `initData`, и заказ
 * привязывается к заказчику — из браузера этого не происходит.
 */
export function artistFromStartParam(): string | null {
  const param = telegram()?.initDataUnsafe?.start_param;

  return param?.startsWith(ARTIST_START_PREFIX)
    ? param.slice(ARTIST_START_PREFIX.length) || null
    : null;
}

export function haptic(type: 'error' | 'success' | 'warning'): void {
  telegram()?.HapticFeedback?.notificationOccurred(type);
}

/**
 * Шапка и фон красятся в цвета текущей темы клиента, а не в зашитые светлые.
 * Хекс-цвета поддерживаются с Bot API 6.9, поэтому для старых версий
 * используются именованные значения.
 */
function syncTheme(app: TelegramWebApp): void {
  applyColorScheme(app.colorScheme === 'dark' ? 'dark' : 'light');

  const supportsHex = app.isVersionAtLeast?.('6.9') ?? false;
  const headerColor = app.themeParams.header_bg_color ?? app.themeParams.bg_color;
  const backgroundColor = app.themeParams.bg_color;

  app.setHeaderColor?.(supportsHex && headerColor ? headerColor : 'bg_color');
  if (supportsHex && backgroundColor) {
    app.setBackgroundColor?.(backgroundColor);
  } else {
    app.setBackgroundColor?.('bg_color');
  }
}

/**
 * Нижняя навигация должна оставаться на месте, когда открыта клавиатура,
 * поэтому высоту берём из стабильного вьюпорта Telegram.
 */
function syncViewport(app: TelegramWebApp): void {
  const heights = viewportHeights(app);

  if (!heights) {
    return;
  }

  document.documentElement.style.setProperty('--app-viewport-height', `${heights.stable}px`);
  document.documentElement.style.setProperty(
    '--app-current-viewport-height',
    `${heights.current}px`,
  );
}

/**
 * Высоты для разметки или `null`, если записывать нечего.
 *
 * Свёрнутое приложение занимает полоску у нижнего края, и его высота в
 * разметку попасть не должна: переменные пишутся инлайном в `<html>` и живут
 * до следующего события. Telegram присылает `viewportChanged` при сворачивании,
 * но не обязан присылать его, когда приложение возвращается из фона, — записав
 * высоту полоски, страница осталась бы такой до перезапуска, а каждый лист
 * открывался бы прижатой к низу полоской.
 */
export function viewportHeights(
  app: Pick<TelegramWebApp, 'isExpanded' | 'viewportHeight' | 'viewportStableHeight'>,
): { stable: number; current: number } | null {
  if (app.isExpanded === false) {
    return null;
  }

  const stable = positiveHeight(app.viewportStableHeight) ?? positiveHeight(app.viewportHeight);
  const current = positiveHeight(app.viewportHeight) ?? stable;

  return stable && current ? { stable, current } : null;
}

function positiveHeight(value: number | undefined): number | null {
  return typeof value === 'number' && value > 0 ? value : null;
}

/** Восстанавливаем высоту только для открытого листа, не запрещая обычное сворачивание. */
export function shouldExpandModalViewport(
  isExpanded: boolean | undefined,
  isStateStable: boolean | undefined,
  modalOpen: boolean,
): boolean {
  return modalOpen && isExpanded === false && isStateStable === true;
}

function applyColorScheme(scheme: 'light' | 'dark'): void {
  document.documentElement.dataset.theme = scheme;
  document.documentElement.style.colorScheme = scheme;
}

function preferredBrowserScheme(): 'light' | 'dark' {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
